import { ColorToken } from '@backrs_ui/common-ui/src/designSystem/colors/colors';
import { EntryTone } from '@backrs_ui/common-ui/src/molecules/MonthCalendar/monthCalendar.types';
import { toneForSection } from '@backrs_ui/grow-ui/src/pages/SatActBooster/utils/planCalendar';

import { Box, SxProps, Theme, Typography } from '@mui/material';

type ChipColors = Readonly<{ bg: ColorToken; border: ColorToken; fg: ColorToken }>;

// Section accent families, matching the calendar's entry tones so a section reads the same color
// on the calendar and in the day detail.
const TONE_COLORS: Record<EntryTone, ChipColors> = {
  brand: { bg: 'Brand/50', border: 'Brand/200', fg: 'Brand/700' },
  indigo: { bg: 'Indigo/50', border: 'Indigo/200', fg: 'Indigo/700' },
  blue: { bg: 'Blue/50', border: 'Blue/200', fg: 'Blue/700' },
  orange: { bg: 'Orange/50', border: 'Orange/200', fg: 'Orange/700' },
  teal: { bg: 'Teal/50', border: 'Teal/200', fg: 'Teal/700' },
  pink: { bg: 'Pink/50', border: 'Pink/200', fg: 'Pink/700' },
  green: { bg: 'Green/50', border: 'Green/200', fg: 'Green/700' },
  yellow: { bg: 'Yellow/50', border: 'Yellow/200', fg: 'Yellow/700' },
  neutral: {
    bg: 'Colors/Background/bg-secondary',
    border: 'Colors/Border/border-secondary',
    fg: 'Colors/Text/text-secondary',
  },
};

type SectionChipProps = Readonly<{
  /** The section label shown in the badge; also drives its color. */
  section: string;
}>;

/** A small section-colored badge, colored by the same section→tone map as the calendar. */
export const SectionChip = ({ section }: SectionChipProps) => {
  const colors = TONE_COLORS[toneForSection(section)];

  return (
    <Box sx={chipSx(colors)}>
      <Typography variant="text-sm-medium" sx={labelSx(colors)}>
        {section}
      </Typography>
    </Box>
  );
};

const chipSx =
  (colors: ChipColors): SxProps<Theme> =>
  ({ backrs }) => ({
    display: 'inline-flex',
    alignItems: 'center',
    paddingInline: backrs.spacing['spacing-md'],
    paddingBlock: backrs.spacing['spacing-xxs'],
    borderRadius: backrs.radius['radius-sm'],
    backgroundColor: backrs.colors[colors.bg],
    border: `1px solid ${backrs.colors[colors.border]}`,
  });

const labelSx =
  (colors: ChipColors): SxProps<Theme> =>
  ({ backrs }) => ({
    color: backrs.colors[colors.fg],
    whiteSpace: 'nowrap',
  });
